<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Tickets</h3>
              <v-btn class="mx-2 mb-2" fab x-small color="primary" @click="openCreateDialog">
                <v-icon dark>mdi-plus</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="5">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search by ticket code or user..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col md="3">
                  <v-select
                    solo
                    rounded
                    v-model="filter.status"
                    :items="statusOptions"
                    label="Filter by Status"
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </section>
              <v-simple-table v-else fixed-header height="450" class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Ticket Code</th>
                      <th class="text-left">Category</th>
                      <th class="text-left">User</th>
                      <th class="text-left">Last Message</th>
                      <th class="text-left">Assigned To</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">Created</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="tickets.length">
                    <tr
                      v-for="(ticket, i) in tickets"
                      :key="`ticket_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                      }"
                    >
                      <td>{{ serialNoStarting + i + 1 }}</td>
                      <td>
                        <span class="font-weight-medium">{{ ticket.ticket_code }}</span>
                      </td>
                      <td>
                        <span v-if="ticket.category">{{ ticket.category.description }}</span>
                        <span v-else class="grey--text">-</span>
                      </td>
                      <td>
                        <span v-if="ticket.user">
                          {{ ticket.user.first_name }} {{ ticket.user.last_name }}
                        </span>
                        <span v-else class="grey--text">-</span>
                      </td>
                      <td>
                        <span v-if="ticket.latest_message" class="grey--text text--darken-1" style="font-size:12px">
                          {{ ticket.latest_message.message | truncate(40) }}
                        </span>
                        <span v-else class="grey--text">-</span>
                      </td>
                      <td>
                        <span v-if="ticket.assigned_admin" class="d-flex align-center">
                          <v-icon small color="orange darken-2" class="mr-1">mdi-account-check</v-icon>
                          {{ ticket.assigned_admin.first_name }} {{ ticket.assigned_admin.last_name }}
                        </span>
                        <v-chip v-else x-small color="grey lighten-2" text-color="grey darken-1">Unassigned</v-chip>
                      </td>
                      <td>
                        <v-chip small :color="statusColor(ticket.status)" text-color="white">
                          {{ statusLabel(ticket.status) }}
                        </v-chip>
                      </td>
                      <td>{{ ticket.created_at | ddmmyyyy }}</td>
                      <td class="text-right">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="primary"
                              :to="`/tickets/${encryptIt(ticket.id)}`"
                            >
                              <v-icon dark>mdi-eye</v-icon>
                            </v-btn>
                          </template>
                          <span>Open Ticket</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="8" class="text-center grey--text py-6">No tickets found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination v-model="page" :length="paginationLength"></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Create Ticket Dialog -->
      <v-dialog v-model="createDialog" max-width="580">
        <v-card rounded="xxl">
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-ticket-plus</v-icon>
            Create New Ticket
            <v-spacer></v-spacer>
            <v-btn icon dark @click="createDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-5">
            <v-select
              v-model="form.ticket_category_id"
              :items="categories"
              item-text="description"
              item-value="id"
              label="Select Category *"
              outlined
              :error-messages="errors.ticket_category_id"
            ></v-select>
            <v-textarea
              v-model="form.message"
              label="Describe your issue *"
              outlined
              rows="4"
              :error-messages="errors.message"
            ></v-textarea>
          </v-card-text>
          <v-card-actions class="pb-4 px-4">
            <v-spacer></v-spacer>
            <v-btn text @click="createDialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="isSaving" @click="createTicket">Create Ticket</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Success Dialog -->
      <v-dialog v-model="successDialog" width="400">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col class="text-center">
                <v-icon large style="color: green; font-size: 70px">mdi-check-circle</v-icon>
                <h3 class="mt-2">Ticket created successfully!</h3>
                <v-btn color="success" class="mt-3" @click="successDialog = false">OK</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "TicketsPage",
  data() {
    return {
      tickets: [],
      categories: [],
      filter: { search_keyword: "", status: null },
      form: { ticket_category_id: null, message: "" },
      errors: {},
      page: 1,
      count: 0,
      serialNoStarting: 0,
      paginationLength: 0,
      isLoading: false,
      isSaving: false,
      createDialog: false,
      successDialog: false,
      statusOptions: [
        { text: "Open", value: "open" },
        { text: "Pending", value: "pending" },
        { text: "Resolved", value: "resolved" },
        { text: "Closed", value: "closed" },
      ],
    };
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
    this.getCategories();
  },
  methods: {
    statusColor(status) {
      if (status === "open") return "blue";
      if (status === "pending") return "orange";
      if (status === "resolved") return "green";
      if (status === "closed") return "grey";
      return "grey";
    },
    statusLabel(status) {
      if (status === "open") return "Open";
      if (status === "pending") return "Pending";
      if (status === "resolved") return "Resolved";
      if (status === "closed") return "Closed";
      return status || "Unknown";
    },
    openCreateDialog() {
      this.form = { ticket_category_id: null, message: "" };
      this.errors = {};
      this.createDialog = true;
    },
    async getCategories() {
      try {
        const res = await axios.get("masters?context=ticket");
        this.categories = res.data.ticket_categories || [];
      } catch (e) {
        console.log(e);
      }
    },
    async getData() {
      this.isLoading = true;
      const params = new URLSearchParams({
        page: this.page,
        rowsPerPage: this.rowsPerPage || 15,
        search_keyword: this.filter.search_keyword || "",
      });
      if (this.filter.status) params.append("status", this.filter.status);
      try {
        const res = await axios.get(`tickets?${params.toString()}`);
        this.tickets = res.data.data || [];
        this.count = res.data.count || 0;
        this.serialNoStarting = (this.page - 1) * (this.rowsPerPage || 15);
        this.paginationLength = Math.ceil(this.count / (this.rowsPerPage || 15));
      } catch (e) {
        console.log(e);
      }
      this.isLoading = false;
    },
    async createTicket() {
      this.errors = {};
      if (!this.form.ticket_category_id) {
        this.errors.ticket_category_id = ["Category is required"];
        return;
      }
      if (!this.form.message || !this.form.message.trim()) {
        this.errors.message = ["Message is required"];
        return;
      }
      this.isSaving = true;
      try {
        const res = await axios.post("tickets", {
          ticket_category_id: this.form.ticket_category_id,
          message: this.form.message.trim(),
        });
        this.createDialog = false;
        this.successDialog = true;
        this.getData();
        if (res.data.data && res.data.data.id) {
          this.$router.push(`/tickets/${this.encryptIt(res.data.data.id)}`);
        }
      } catch (e) {
        if (e.response && e.response.data && e.response.data.errors) {
          this.errors = e.response.data.errors;
        }
        console.log(e);
      }
      this.isSaving = false;
    },
  },
  filters: {
    truncate(value, length) {
      if (!value) return "";
      return value.length > length ? value.substring(0, length) + "..." : value;
    },
  },
};
</script>
